import React, { useMemo, useState } from 'react';
import { useStore, useSupplierById } from '../store-hybrid';
import { BrandLogo } from '../components/BrandLogo';
import { SellCarModal } from '../components/SellCarModal';
import { formatSimpleCurrency } from '../utils/currencyUtils';
import { handleImageError } from '../utils/imageUtils';
import { useReadOnly } from '../hooks/useReadOnly';
import { BrandId, Car } from '../types';

export const SalesPage: React.FC = () => {
  const { brands, models, cars, updateCar, addFundTransaction } = useStore();
  
  // Filter states
  const [filterBrand, setFilterBrand] = useState<BrandId>('');
  const [filterPriceRange, setFilterPriceRange] = useState<'all' | 'under-20k' | '20k-40k' | 'over-40k'>('all');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Modal states
  const [showSellModal, setShowSellModal] = useState(false);
  const [selectedCarForSale, setSelectedCarForSale] = useState<Car | null>(null);

  // Get cars available for sale (in garage, not rented, not sold, has sale price)
  const availableForSale = useMemo(() => {
    let filtered = cars.filter(car => 
      car.inGarage && 
      !car.isRented && 
      !car.isSold && 
      car.salePrice && 
      car.salePrice > 0
    );
    
    if (filterBrand) {
      filtered = filtered.filter(car => car.brandId === filterBrand);
    }
    
    if (filterPriceRange !== 'all') {
      filtered = filtered.filter(car => {
        if (!car.salePrice) return false;
        switch (filterPriceRange) {
          case 'under-20k': return car.salePrice < 2000000;
          case '20k-40k': return car.salePrice >= 2000000 && car.salePrice <= 4000000;
          case 'over-40k': return car.salePrice > 4000000;
          default: return true;
        }
      });
    }
    
    return filtered.sort((a, b) => (a.salePrice || 0) - (b.salePrice || 0));
  }, [cars, filterBrand, filterPriceRange]);

  const soldCars = cars.filter(car => car.isSold);

  const handleMarkForSale = (carId: string) => {
    updateCar(carId, {
      isForSale: true,
    });
    alert('Car marked for sale!');
  };

  const handleRemoveFromSale = (carId: string) => {
    if (window.confirm('Remove this car from sale listings?')) {
      updateCar(carId, {
        isForSale: false,
      });
      alert('Car removed from sale listings!');
    }
  };

  const handleSellCar = (carId: string) => {
    const car = cars.find(c => c.id === carId);
    if (car) {
      setSelectedCarForSale(car);
      setShowSellModal(true);
    }
  };

  const handleSellConfirm = async (customerName: string, customerPhone: string, customerLicense: string, sellingPrice: number) => {
    if (selectedCarForSale) {
      // Update car as sold
      await updateCar(selectedCarForSale.id, {
        isSold: true,
        soldDate: new Date(),
        isForSale: false,
        soldToCustomer: customerName,
        soldToCustomerPhone: customerPhone,
        soldToCustomerLicense: customerLicense,
        finalSalePrice: sellingPrice,
      });

      // Add full sale price to fund
      // Get brand and model names for better description
      const brand = brands.find(b => b.id === selectedCarForSale.brandId);
      const model = models.find(m => m.id === selectedCarForSale.modelId);
      const carName = `${brand?.name || 'Unknown'} ${model?.name || 'Unknown'}`;
      
      // Add full sale price to fund as "Car Sale" transaction
      await addFundTransaction({
        type: 'add_money',
        amount: sellingPrice,
        description: `Car Sale Revenue - ${carName}`,
        category: 'sale'
      });

      // Calculate profit for display message
      const purchasePrice = selectedCarForSale.purchasePrice || 0;
      const profit = sellingPrice - purchasePrice;
      
      let profitMessage = '';
      if (profit > 0) {
        profitMessage = `Net profit: ${formatSimpleCurrency(profit)}.`;
      } else if (profit < 0) {
        profitMessage = `Net loss: ${formatSimpleCurrency(Math.abs(profit))}.`;
      } else {
        profitMessage = 'Break-even sale.';
      }
      
      alert(`Car sold to ${customerName} for ${formatSimpleCurrency(sellingPrice)}! Full revenue added to fund. ${profitMessage}`);
    }
    setShowSellModal(false);
    setSelectedCarForSale(null);
  };

  const stats = [
    {
      label: 'Available for Sale',
      value: availableForSale.length,
      icon: (
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      )
    },
    {
      label: 'Cars Sold',
      value: soldCars.length,
      icon: (
        <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      label: 'Total Revenue',
      value: (() => {
        const soldCarsWithPrices = soldCars.filter(car => car.finalSalePrice);
        if (soldCarsWithPrices.length === 0) return formatSimpleCurrency(0);
        
        const totalRevenue = soldCarsWithPrices.reduce((sum, car) => {
          return sum + (car.finalSalePrice || 0);
        }, 0);
        
        return formatSimpleCurrency(totalRevenue);
      })(),
      icon: (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      )
    },
    {
      label: 'Net Profit',
      value: (() => {
        const soldCarsWithPrices = soldCars.filter(car => car.finalSalePrice && car.purchasePrice);
        if (soldCarsWithPrices.length === 0) return formatSimpleCurrency(0);
        
        const totalProfit = soldCarsWithPrices.reduce((sum, car) => {
          const profit = (car.finalSalePrice || 0) - (car.purchasePrice || 0);
          return sum + profit;
        }, 0);
        
        return formatSimpleCurrency(Math.round(totalProfit / soldCarsWithPrices.length));
      })(),
      icon: (
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white dark:text-white mb-2">
            💰 Car Sales
          </h1>
          <p className="text-gray-600 dark:text-gray-400 dark:text-gray-300">
            Manage car sales - available cars from your garage
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                </div>
                <div className="p-3 rounded-xl bg-gray-50">
                  {stat.icon}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Collapsible Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mb-8 overflow-hidden">
          {/* Filter Header - Always Visible */}
          <div 
            className="p-4 sm:p-6 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 dark:border-gray-700"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h3>
                {!filtersExpanded && (
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                      {filterBrand ? brands.find(b => b.id === filterBrand)?.name : 'All Brands'}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                      {filterPriceRange === 'all' ? 'All Prices' : 
                       filterPriceRange === 'under-20k' ? 'Under 2M DA' :
                       filterPriceRange === '20k-40k' ? '2M-4M DA' : 'Over 4M DA'}
                    </span>
                  </div>
                )}
              </div>
              <svg 
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${filtersExpanded ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Filter Content - Expandable */}
          <div className={`transition-all duration-300 ease-in-out ${filtersExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Brand
              </label>
              <select
                value={filterBrand}
                onChange={(e) => setFilterBrand(e.target.value as BrandId)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All Brands</option>
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price Range
              </label>
              <select
                value={filterPriceRange}
                onChange={(e) => setFilterPriceRange(e.target.value as any)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Prices</option>
                    <option value="under-20k">Under 2,000,000 DA</option>
                    <option value="20k-40k">2,000,000 - 4,000,000 DA</option>
                    <option value="over-40k">Over 4,000,000 DA</option>
              </select>
                </div>
              </div>

              {/* Clear Filters Button */}
              {(filterBrand || filterPriceRange !== 'all') && (
                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setFilterBrand('');
                      setFilterPriceRange('all');
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Available for Sale Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Available for Sale</h2>
          {availableForSale.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {availableForSale.map((car) => (
                <SaleCarCard
                  key={car.id}
                  car={car}
                  onMarkForSale={() => handleMarkForSale(car.id)}
                  onRemoveFromSale={() => handleRemoveFromSale(car.id)}
                  onSell={() => handleSellCar(car.id)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No cars available for sale</h3>
              <p className="text-gray-600 dark:text-gray-400">
                {cars.filter(car => car.inGarage && car.salePrice).length === 0 
                  ? 'Add cars with sale prices to your garage first.'
                  : 'Try adjusting your filters or check back later.'
                }
              </p>
            </div>
          )}
        </div>

        {/* Sold Cars Section */}
        {soldCars.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Recently Sold</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {soldCars.slice(-8).map((car) => (
                <SoldCarCard key={car.id} car={car} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sell Car Modal */}
      {selectedCarForSale && (
        <SellCarModal
          isOpen={showSellModal}
          onClose={() => {
            setShowSellModal(false);
            setSelectedCarForSale(null);
          }}
          onConfirm={handleSellConfirm}
          carInfo={{
            brand: brands.find(b => b.id === selectedCarForSale.brandId)?.name || '',
            model: models.find(m => m.id === selectedCarForSale.modelId)?.name || '',
            plate: selectedCarForSale.plate,
            currentPrice: selectedCarForSale.salePrice
          }}
        />
      )}
    </div>
  );
};

// Available Sale Car Card
const SaleCarCard: React.FC<{ 
  car: Car; 
  onMarkForSale: () => void;
  onRemoveFromSale: () => void;
  onSell: () => void;
}> = ({ car, onMarkForSale, onRemoveFromSale, onSell }) => {
  const { brands, models } = useStore();
  const brand = brands.find(b => b.id === car.brandId);
  const model = models.find(m => m.id === car.modelId);
  const supplier = useSupplierById(car.supplierId || '');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const hasMultipleImages = car.images && car.images.length > 1;
  const totalImages = car.images?.length || 0;

  const nextImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev + 1) % totalImages);
    }
  };

  const prevImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
      {/* Car Image */}
      <div className="w-full h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl mb-4 overflow-hidden relative group">
        {car.images && car.images.length > 0 ? (
          <>
          <img 
              src={car.images[currentImageIndex]} 
            alt={`${brand?.name} ${model?.name}`}
              className="w-full h-full object-cover transition-opacity duration-300"
              onError={(e) => handleImageError(e, 'car', 400, 300)}
            />
            
            {/* Navigation Buttons */}
            {hasMultipleImages && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-opacity-70"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-opacity-70"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                
                {/* Image Counter */}
                <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {currentImageIndex + 1}/{totalImages}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-16 h-16 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
            </svg>
          </div>
        )}
        {car.isForSale && (
          <div className="absolute top-2 right-2 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
            For Sale
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-start space-x-3">
          <BrandLogo brandId={car.brandId} size="md" />
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
              {brand?.name} {model?.name}
            </h3>
            {car.year && (
              <p className="text-sm text-gray-500">{car.year} • {car.color}</p>
            )}
          </div>
        </div>

        {/* Car Details */}
        <div className="grid grid-cols-2 gap-2 text-sm">
          {car.mileageKm && (
            <div className="flex items-center space-x-1">
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-gray-600 dark:text-gray-400">{car.mileageKm.toLocaleString()} km</span>
            </div>
          )}
          
          {car.transmission && (
            <div className="flex items-center space-x-1">
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
              </svg>
              <span className="text-gray-600 dark:text-gray-400 capitalize">{car.transmission}</span>
            </div>
          )}
        </div>

        {/* Payment Status */}
        <div className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Payment Status:</span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            car.isPaid ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
          }`}>
            {car.isPaid ? 'Paid' : 'Unpaid'}
          </span>
        </div>
        
        {/* Availability Notice */}
        {!car.isPaid && (
          <div className="flex items-center space-x-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span className="text-xs text-amber-700 dark:text-amber-300">Sale only - rental disabled</span>
          </div>
        )}

        {/* Rating */}
        {car.rating && car.rating > 0 && (
          <div className="flex items-center space-x-1">
            <svg className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className="text-sm font-medium text-gray-700">{car.rating}</span>
          </div>
        )}

        {/* Price and Profit */}
        <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="text-2xl font-bold text-green-600 mb-2">
            {formatSimpleCurrency(car.salePrice || 0)}
          </div>
          
          {/* Purchase Price and Profit */}
          {car.purchasePrice && (
            <div className="space-y-1 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Purchase:</span>
                <span className="text-gray-700 dark:text-gray-300">{formatSimpleCurrency(car.purchasePrice)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Profit:</span>
                <span className={`font-medium ${
                  (car.salePrice || 0) - (car.purchasePrice || 0) >= 0 
                    ? 'text-green-600' 
                    : 'text-red-600'
                }`}>
                  {formatSimpleCurrency((car.salePrice || 0) - (car.purchasePrice || 0))}
                </span>
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="space-y-2">
            {!car.isForSale ? (
              <button
                onClick={onMarkForSale}
                className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
              >
                Mark for Sale
              </button>
            ) : (
              <div className="space-y-2">
                <button
                  onClick={onSell}
                  className="w-full px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors font-medium"
                >
                  Mark as Sold
                </button>
                <button
                  onClick={onRemoveFromSale}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Remove from Sale
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Sold Car Card
const SoldCarCard: React.FC<{ car: Car }> = ({ car }) => {
  const { brands, models } = useStore();
  const brand = brands.find(b => b.id === car.brandId);
  const model = models.find(m => m.id === car.modelId);
  const supplier = useSupplierById(car.supplierId || '');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const hasMultipleImages = car.images && car.images.length > 1;
  const totalImages = car.images?.length || 0;

  const nextImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev + 1) % totalImages);
    }
  };

  const prevImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 opacity-75">
      {/* Car Image */}
      <div className="w-full h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl mb-4 overflow-hidden relative group">
        {car.images && car.images.length > 0 ? (
          <>
          <img 
              src={car.images[currentImageIndex]} 
            alt={`${brand?.name} ${model?.name}`}
              className="w-full h-full object-cover transition-opacity duration-300"
              onError={(e) => handleImageError(e, 'car', 400, 300)}
            />
            
            {/* Navigation Buttons */}
            {hasMultipleImages && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-opacity-70"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-opacity-70"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                
                {/* Image Counter */}
                <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {currentImageIndex + 1}/{totalImages}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-16 h-16 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
            </svg>
          </div>
        )}
        <div className="absolute top-2 right-2 px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
          Sold
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-start space-x-3">
          <BrandLogo brandId={car.brandId} size="md" />
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
              {brand?.name} {model?.name}
            </h3>
            {car.year && (
              <p className="text-sm text-gray-500">{car.year} • {car.color}</p>
            )}
          </div>
        </div>

        {/* Sale Info */}
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">Final Price:</span>
            <span className="font-medium text-green-600">{formatSimpleCurrency(car.finalSalePrice || car.salePrice || 0)}</span>
          </div>
          {car.salePrice && car.finalSalePrice && car.salePrice !== car.finalSalePrice && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Listed Price:</span>
              <span className="text-gray-500">{formatSimpleCurrency(car.salePrice)}</span>
            </div>
          )}
          {car.purchasePrice && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Purchase Price:</span>
              <span className="text-gray-500">{formatSimpleCurrency(car.purchasePrice)}</span>
            </div>
          )}
          {supplier && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Supplier:</span>
              <span className="text-gray-500">{supplier.name}</span>
            </div>
          )}
          {car.soldToCustomer && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Customer:</span>
                <span className="font-medium text-purple-600">{car.soldToCustomer}</span>
              </div>
              {car.soldToCustomerPhone && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Phone:</span>
                  <span className="text-gray-700">{car.soldToCustomerPhone}</span>
                </div>
              )}
              {car.soldToCustomerLicense && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">License:</span>
                  <span className="text-gray-700">{car.soldToCustomerLicense}</span>
                </div>
              )}
            </div>
          )}
          {car.soldDate && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Sold Date:</span>
              <span className="font-medium">
                {(() => {
                  // Handle Firestore Timestamp objects
                  if (typeof car.soldDate === 'object' && 'seconds' in car.soldDate) {
                    return new Date((car.soldDate as any).seconds * 1000).toLocaleDateString();
                  }
                  return new Date(car.soldDate).toLocaleDateString();
                })()}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
